import { useState, useEffect, useCallback, useRef } from "react";

export interface TypingState {
  cursorPos: number;
  errors: number;
  errorPositions: Set<number>;
  startTime: number | null;
  endTime: number | null;
  isComplete: boolean;
}

export interface TypingEngine {
  state: TypingState;
  wpm: number;
  accuracy: number;
  currentChar: string;
  reset: () => void;
}

function makeInitial(): TypingState {
  return {
    cursorPos: 0,
    errors: 0,
    errorPositions: new Set(),
    startTime: null,
    endTime: null,
    isComplete: false,
  };
}

export function useTypingEngine(snippet: string): TypingEngine {
  // Use ref as the single source of truth so rapid keypresses never get stale state
  const stateRef = useRef<TypingState>(makeInitial());
  const snippetRef = useRef(snippet);
  const [, forceRender] = useState(0);

  const reset = useCallback(() => {
    stateRef.current = makeInitial();
    forceRender((n) => n + 1);
  }, []);

  // Reset when snippet changes
  useEffect(() => {
    snippetRef.current = snippet;
    stateRef.current = makeInitial();
    forceRender((n) => n + 1);
  }, [snippet]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const s = stateRef.current;
      const code = snippetRef.current;

      if (s.isComplete) return;

      // Ignore modifier-only keys and navigation keys
      if (
        e.key === "Shift" ||
        e.key === "Control" ||
        e.key === "Alt" ||
        e.key === "Meta" ||
        e.key === "CapsLock" ||
        e.key === "Tab" ||
        e.key === "Escape" ||
        e.key === "Backspace" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "F1" ||
        e.key === "F2" ||
        e.key === "F3" ||
        e.key === "F4" ||
        e.key === "F5" ||
        e.key === "F6" ||
        e.key === "F7" ||
        e.key === "F8" ||
        e.key === "F9" ||
        e.key === "F10" ||
        e.key === "F11" ||
        e.key === "F12"
      ) {
        return;
      }

      e.preventDefault();

      const expected = code[s.cursorPos];
      if (expected === undefined) return;

      const typed = e.key === "Enter" ? "\n" : e.key;
      const now = Date.now();
      const startTime = s.startTime ?? now;

      if (typed === expected) {
        // Correct key — advance cursor
        const newPos = s.cursorPos + 1;
        const isComplete = newPos >= code.length;
        stateRef.current = {
          ...s,
          cursorPos: newPos,
          startTime,
          endTime: isComplete ? now : null,
          isComplete,
        };
      } else {
        // Wrong key — do NOT advance, just count the error
        const newErrors = new Set(s.errorPositions);
        newErrors.add(s.cursorPos);
        stateRef.current = {
          ...s,
          errors: s.errors + 1,
          errorPositions: newErrors,
          startTime,
        };
      }

      forceRender((n) => n + 1);
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const state = stateRef.current;

  const elapsed = state.startTime
    ? ((state.endTime ?? Date.now()) - state.startTime) / 60000
    : 0;

  const wpm = elapsed > 0 ? Math.round(state.cursorPos / 5 / elapsed) : 0;
  const accuracy =
    state.cursorPos + state.errors > 0
      ? Math.round((state.cursorPos / (state.cursorPos + state.errors)) * 100)
      : 100;

  const currentChar = snippet[state.cursorPos] ?? "";

  return { state, wpm, accuracy, currentChar, reset };
}
